import type { ReactNode } from 'react';
import { useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { UIStateProvider } from '@/shared/state/UIStateContext';
import { ActiveCompanyProvider } from '@/shared/state/ActiveCompanyContext';
import { TypeHeroToastProvider, ToastStack } from '@/shared/components/toast-system';
import { setQueryClientSingleton } from '@/shared/cache/queryClientSingleton';

type ProvidersProps = {
  children: ReactNode;
};

const Providers = ({ children }: ProvidersProps) => {
  const isDev = import.meta.env.DEV;
  const [queryClient] = useState(() => {
    const client = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: 30 * 1000,
          gcTime: 10 * 60 * 1000,
          retry: 1,
          refetchOnWindowFocus: false,
        },
      },
    });
    setQueryClientSingleton(client);
    return client;
  });

  return (
    <QueryClientProvider client={queryClient}>
      <TypeHeroToastProvider>
        <UIStateProvider>
          <ActiveCompanyProvider>
            {children}
          </ActiveCompanyProvider>
        </UIStateProvider>
        <ToastStack />
      </TypeHeroToastProvider>
      {isDev ? <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" /> : null}
    </QueryClientProvider>
  );
};

export default Providers;
